import { useMutation, useQueryClient } from '@tanstack/react-query';
import { calendarApi } from '../api';
import { CalendarEvent, CalendarEventMetrics } from '@demind-inc/core';
import { cloneDeep } from 'lodash';

interface UseUpdateCalendarEventEnergyParams {
  calendarId: string;
  eventId: string;
  eventMetrics: CalendarEventMetrics;
  mutateParams?: {
    date?: string;
  };
}

export function useUpdateCalendarEventEnergy() {
  const queryClient = useQueryClient();

  const updateCalendarEventEnergyMutation = useMutation({
    mutationFn: ({ calendarId, eventId, eventMetrics }: UseUpdateCalendarEventEnergyParams) => {
      return calendarApi.updateCalendarEventEnergy(calendarId, eventId, eventMetrics);
    },
    onMutate: async ({ eventId: targetEventId, eventMetrics }) => {
      // Cancel any outgoing refetches to avoid overwriting our optimistic update
      await queryClient.cancelQueries({ queryKey: ['lifestack.calendar.events'] });

      // Snapshot the previous value for rollback
      const previousQueries = queryClient.getQueriesData<CalendarEvent[]>({
        queryKey: ['lifestack.calendar.events'],
      });

      // Optimistically update all calendar.events queries
      queryClient.setQueriesData<CalendarEvent[]>(
        { queryKey: ['lifestack.calendar.events'] },
        (prevEvents) => {
          if (!prevEvents) {
            return prevEvents;
          }
          const newEvents = cloneDeep(prevEvents);
          return newEvents.map((event) => {
            if (event.eventId === targetEventId) {
              return {
                ...event,
                eventMetrics: {
                  ...event.eventMetrics,
                  ...eventMetrics,
                },
              };
            }
            return event;
          });
        }
      );

      // Return context with previous queries for rollback
      return { previousQueries };
    },
    onSuccess: (_, { mutateParams }) => {
      queryClient.invalidateQueries({ queryKey: ['lifestack.calendar.events'] });

      queryClient.invalidateQueries({
        queryKey: mutateParams?.date
          ? ['lifestack.circadian', { date: mutateParams.date }]
          : ['lifestack.circadian'],
      });
    },
  });

  return {
    updateCalendarEventEnergy: updateCalendarEventEnergyMutation.mutateAsync,
    ...updateCalendarEventEnergyMutation,
  };
}
